import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'Service Areas', path: '/service-areas' },
    { name: 'About', path: '/about' },
    { name: 'Reviews', path: '/reviews' },
    { name: 'FAQ', path: '/faq' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <>
      {/* Top Bar */}
      <div className="bg-primary-600 text-white py-2 px-4">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between text-sm">
          <div className="flex items-center gap-6">
            <a href="tel:2814564556" className="flex items-center gap-2 hover:text-accent-300 transition-colors whitespace-nowrap">
              <i className="fas fa-phone-alt"></i>
              <span className="hidden sm:inline">(281) 456-4556</span>
            </a>
            <a href="mailto:service@77waterwell.com" className="hidden md:flex items-center gap-2 hover:text-accent-300 transition-colors whitespace-nowrap">
              <i className="fas fa-envelope"></i>
              <span>service@77waterwell.com</span>
            </a>
          </div>
          <div className="flex items-center gap-2 text-xs sm:text-sm whitespace-nowrap">
            <i className="far fa-clock"></i>
            <span>Mon-Fri: 8AM-5PM | 24/7 Emergency</span>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <motion.header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-lg' : 'bg-white/95 backdrop-blur-sm'
        }`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <nav className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 group">
              <img
                src="https://77waterwell.com/wp-content/uploads/2023/10/77-Water-Well-Red-Blue-Logo-1.png"
                alt="77 Water Well Inc. Logo"
                className="h-14 w-14 object-contain transition-transform group-hover:scale-105"
              />
              <div className="hidden sm:block">
                <div className="text-xl font-display font-bold text-gray-900 whitespace-nowrap">77 Water Well Inc.</div>
                <div className="text-xs text-gray-600 whitespace-nowrap">Expert Water Well Services</div>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-sm font-medium transition-colors hover:text-primary-600 whitespace-nowrap ${
                    location.pathname === link.path ? 'text-primary-600' : 'text-gray-700'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex items-center gap-3">
              <a
                href="tel:2814564556"
                className="hidden sm:flex items-center gap-2 bg-primary-600 text-white px-5 py-2.5 rounded-lg font-semibold hover:bg-primary-700 transition-all hover:shadow-lg whitespace-nowrap"
              >
                <i className="fas fa-phone-alt"></i>
                <span className="hidden md:inline">Call Now</span>
              </a>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden p-2 text-gray-700 hover:text-primary-600 transition-colors"
                aria-label="Toggle menu"
              >
                <div className="w-6 h-6 flex items-center justify-center">
                  {isMobileMenuOpen ? (
                    <i className="fas fa-times text-xl"></i>
                  ) : (
                    <i className="fas fa-bars text-xl"></i>
                  )}
                </div>
              </button>
            </div>
          </div>
        </nav>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden border-t border-gray-200 bg-white"
            >
              <div className="max-w-7xl mx-auto px-4 py-4 space-y-2">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`block px-4 py-3 rounded-lg font-medium transition-colors ${
                      location.pathname === link.path
                        ? 'bg-primary-50 text-primary-600'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {link.name}
                  </Link>
                ))}
                <a
                  href="tel:2814564556"
                  className="block sm:hidden bg-primary-600 text-white px-4 py-3 rounded-lg font-semibold text-center whitespace-nowrap"
                >
                  <i className="fas fa-phone-alt mr-2"></i>
                  Call (281) 456-4556
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.header>
    </>
  );
};

export default Header;